'use client';

import { useState, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import AuthLayout from '@/components/auth/auth-layout';
import PasswordInput from '@/components/auth/password-input';
import SocialLogin from '@/components/auth/social-login';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/lib/auth-context';
import { authApi } from '@/lib/api/auth-api';
import { AlertCircle } from 'lucide-react';
import { saveTokenCookie, saveUserCookie } from '@/lib/cookie-utils';

function LoginContent() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const { login } = useAuth();

    const [formData, setFormData] = useState({
        emailOrUsername: '',
        password: '',
        rememberMe: false,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const getRedirectUrl = () => searchParams.get('redirect') || '/';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await login(formData);
            router.replace(getRedirectUrl());
        } catch (err: any) {
            setError(err.message || 'Login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleLogin = async (accessToken: string) => {
        setError('');
        setIsLoading(true);
        try {
            const response = await authApi.googleSSO(accessToken, 'GUEST');
            if (!response.success || !response.user) {
                throw new Error(response.message || 'Google login failed');
            }
            const token = response.key;
            if (token) {
                localStorage.setItem('key', token);
                saveTokenCookie(token, false);
            }
            window.location.href = getRedirectUrl();
            // router.replace();
        } catch (err: any) {
            setError(err.message || 'Google login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthLayout
            title="Welcome Back"
            subtitle="Log in to your Veentix account"
        >
            <div className="space-y-6">
                {/* Google SSO */}
                <SocialLogin
                    onGoogleSuccess={handleGoogleLogin}
                    isLoading={isLoading}
                    label="Continue with Google"
                />

                {/* Divider */}
                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
                    </div>
                </div>

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="emailOrUsername">Username</Label>
                        <Input
                            id="emailOrUsername"
                            type="text"
                            placeholder="username"
                            value={formData.emailOrUsername}
                            onChange={(e) =>
                                setFormData({ ...formData, emailOrUsername: e.target.value })
                            }
                            required
                            disabled={isLoading}
                        />
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="password">Password</Label>
                            <Link
                                href="/auth/forgot-password"
                                className="text-sm text-primary hover:underline"
                            >
                                Forgot password?
                            </Link>
                        </div>
                        <PasswordInput
                            value={formData.password}
                            onChange={(value) => setFormData({ ...formData, password: value })}
                            placeholder="Enter your password"
                        />
                    </div>

                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="rememberMe"
                            checked={formData.rememberMe}
                            onCheckedChange={(checked) =>
                                setFormData({ ...formData, rememberMe: checked as boolean })
                            }
                        />
                        <label
                            htmlFor="rememberMe"
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                            Remember me
                        </label>
                    </div>

                    <Button type="submit" className="w-full" disabled={isLoading}>
                        {isLoading ? 'Logging in...' : 'Log In'}
                    </Button>
                </form>

                {/* Sign Up Link */}
                <div className="space-y-2 text-center text-sm">
                    <div>
                        <span className="text-muted-foreground">login as organizer? </span>
                        <Link href={process.env.NEXT_PUBLIC_URL_LOGIN_ADMIN || "/auth/login"}
                            className="text-primary hover:underline font-medium">Login</Link>
                    </div>
                    <div>
                        <span className="text-muted-foreground">Don't have an account? </span>
                        <Link href="/auth/register" className="text-primary hover:underline font-medium">
                            Sign up
                        </Link>
                    </div>
                    <div>
                        <span className="text-muted-foreground">Sudah login? </span>
                        <Link href="/auth/register-eo" className="text-primary hover:underline font-medium">
                            Daftar sebagai EO
                        </Link>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
}

export default function LoginPage() {
    return (
        <Suspense fallback={
            <AuthLayout title="Loading..." subtitle="Please wait while we prepare the login page">
                <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            </AuthLayout>
        }>
            <LoginContent />
        </Suspense>
    );
}
